import {
  AddJsxChange,
  AddAndRemoveJsxChange,
  AnyChangeLedgerItem,
  ChangeType,
  DuplicateChange,
  EditTextChange,
  RemoveJsxChange,
  UndoChangeFields,
  UpdateClassesChange,
  UpdateDesignTokensChange,
  UpdateStoryboardsChange,
  WrapDivChange,
  WrapStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  applyUpdateClassesChange,
  undoUpdateClassesChange,
} from "../utils/applyUpdateClasses";
import {
  applyUpdateDesignTokensChange,
  undoUpdateDesignTokensChange,
} from "../utils/applyUpdateDesignTokens";
import { applyWrapDivChange, undoWrapDivChange } from "../utils/applyWrapInDiv";
import {
  getCurrentLookups,
  NavTreeUpdates,
  updateAllStoreLookups,
} from "../utils/navTreeLookupUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllSelectedElementKeys,
  setHoveredElementKey,
  setSelectedElementKey,
} from "@/store/iframeSlice";
import { TempoElement } from "@/utils/tempoElement";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { NavTreeNode } from "@/utils/types";
import {
  applyRemoveJsxChange,
  undoRemoveJsxChange,
} from "../utils/applyRemoveJsx";
import {
  applyEditTextChange,
  undoEditTextChange,
} from "../utils/applyEditText";
import {
  applyDuplicateJsxChange,
  undoDuplicateJsxChange,
} from "../utils/applyDuplicateJsx";
import { applyAddJsxChange, undoAddJsxChange } from "../utils/applyAddJsx";
import {
  applyStoryboardsUpdate,
  undoStoryboardsUpdate,
} from "../utils/applyStoryboardsUpdate";
import {
  applyWrapStoryboardsUpdate,
  undoWrapStoryboardsUpdate,
} from "../utils/applyWrapStoryboardsUpdate";
import {
  useAutoUpdateRef,
  useCanvasId,
  useInitialRenderEffect,
} from "@/utils/utils";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { ApplyInstantUpdateProps } from "../utils/utils";

// Make sure to update this when adding new change types to the instant update system
export const CHANGE_TYPES_WITH_INSTANT_UPDATE = [
  ChangeType.UPDATE_CLASSES,
  ChangeType.UPDATE_DESIGN_TOKENS,
  ChangeType.WRAP_DIV,
  ChangeType.REMOVE_JSX,
  ChangeType.EDIT_TEXT,
  ChangeType.DUPLICATE,
  ChangeType.ADD_JSX,
  ChangeType.ADD_AND_REMOVE_JSX,
  ChangeType.UPDATE_STORYBOARD,
  ChangeType.WRAP_STORYBOARDS,
  ChangeType.UPDATE_PAGE,
  ChangeType.CREATE_PAGE,
  ChangeType.DELETE_PAGE,
  ChangeType.ADD_LIBRARY_COMPONENT,
  ChangeType.REMOVE_LIBRARY_COMPONENT,
];

interface UseApplyInstantUpdatesProps {
  storyboardId: string;
  iframeDocRef: React.RefObject<Document | null>;
}

export interface ApplyInstantUpdates {
  navTreeUpdates: NavTreeUpdates;
  iframeDoc: Document;
  storyboardId: string;
  instantUpdateToApply: AnyChangeLedgerItem;

  // From the reapply operation in the iframe pool
  fromReapply?: boolean;

  // Fully skip updating the nav tree
  skipUpdateNavTree?: boolean;
}

export const useApplyInstantUpdates = () => {
  const dispatch = useDispatch();

  const { selectedElement } = useSelectedNavTreeNode();
  const selectedElementRef = useAutoUpdateRef(selectedElement);

  const canvasId = useCanvasId();
  const canvasIdRef = useAutoUpdateRef(canvasId);

  const throwawayUpdatesClasses = useRef<{
    [codebaseId: string]: {
      class: string;
      selectorId: string;
    }[];
  }>({});

  const updateElementKeysForChange = (
    changeItem: AnyChangeLedgerItem,
    isUndo: boolean,
  ) => {
    // Immediately set the new selected element keys to prevent any delay in the outlines updating
    let elementKeyToSelectAfterInstantUpdate =
      changeItem.getElementKeyToSelectAfterInstantUpdate();
    let elementKeysToMultiselectAfterInstantUpdate =
      changeItem.getElementKeysToMultiselectAfterInstantUpdate();

    if (isUndo) {
      elementKeyToSelectAfterInstantUpdate =
        changeItem.getElementKeyToSelectAfterUndoInstantUpdate();

      elementKeysToMultiselectAfterInstantUpdate =
        changeItem.getElementKeysToMultiselectAfterUndoInstantUpdate();
    }

    if (elementKeyToSelectAfterInstantUpdate !== undefined) {
      dispatch(setSelectedElementKey(elementKeyToSelectAfterInstantUpdate));
    }

    if (elementKeysToMultiselectAfterInstantUpdate !== undefined) {
      dispatch(
        setAllSelectedElementKeys({
          multiSelectedElementKeys: elementKeysToMultiselectAfterInstantUpdate,
          selectedElementKey:
            elementKeyToSelectAfterInstantUpdate ||
            selectedElementRef.current?.getKey(),
        }),
      );
    }
  };

  const applyInstantUpdate = ({
    iframeDoc,
    navTreeUpdates,
    storyboardId,
    instantUpdateToApply,
    fromReapply,
    skipUpdateNavTree,
  }: ApplyInstantUpdates) => {
    let coreChange: AnyChangeLedgerItem = instantUpdateToApply,
      isUndo = false;
    if (coreChange.type === ChangeType.REDO) {
      coreChange = instantUpdateToApply.changeFields.changeToRedo;
    } else if (coreChange.type === ChangeType.UNDO) {
      coreChange = coreChange.changeFields.changeToUndo;
      isUndo = true;
    }

    // Instant update was previously sent but does not apply to this storyboard, we can skip it
    if (
      coreChange.getInstantUpdateSent() &&
      coreChange.storyboardsAppliedTo.size !== 0 &&
      !coreChange.storyboardsAppliedTo.has(storyboardId)
    ) {
      return;
    }

    // Small hack: easier to just store and re-apply the instant update success
    const prevInstantUpdateFailed = coreChange.instantUpdateFailed;

    const instantUpdateProps = {
      iframeDocument: iframeDoc,
      navTreeUpdates,
      storyboardId,
      skipUpdateNavTree,
      persistentData: {
        storyboardsAppliedTo: new Set([...coreChange.storyboardsAppliedTo]),
        instantUpdateData: {
          ...(coreChange.instantUpdateData || {}),
        },
        instantUpdateFailed: coreChange.instantUpdateFailed,
      },
      change: coreChange,
    } as ApplyInstantUpdateProps;

    let recreateLookups = false;
    if (isUndo) {
      if (instantUpdateProps.change.type === ChangeType.UPDATE_STORYBOARD) {
        if (instantUpdateProps.change.changeFields.additionalGroupedChange) {
          instantUpdateProps.change =
            coreChange.changeFields.additionalGroupedChange;
        } else if (
          instantUpdateProps.change.changeFields.createStoryboardFields
            ?.removeJsxChangeFields
        ) {
          instantUpdateProps.change = new RemoveJsxChange(
            instantUpdateProps.change.changeFields.createStoryboardFields
              .removeJsxChangeFields,
          );
        }
      }

      if (instantUpdateProps.change.type === ChangeType.UPDATE_CLASSES) {
        undoUpdateClassesChange({
          ...instantUpdateProps,
          throwawayUpdatesClasses: throwawayUpdatesClasses.current,
        });
      } else if (instantUpdateProps.change.type === ChangeType.WRAP_DIV) {
        undoWrapDivChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.REMOVE_JSX) {
        undoRemoveJsxChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.EDIT_TEXT) {
        undoEditTextChange(instantUpdateProps);
      } else if (instantUpdateProps.change.type === ChangeType.DUPLICATE) {
        undoDuplicateJsxChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.ADD_JSX) {
        undoAddJsxChange(instantUpdateProps);
        recreateLookups = true;
      } else if (
        instantUpdateProps.change.type === ChangeType.ADD_AND_REMOVE_JSX
      ) {
        // Undo both operations for AddAndRemoveJsxChange (in reverse order)
        const addAndRemoveChangeToUndo =
          instantUpdateProps.change as AddAndRemoveJsxChange;

        // Check if this was a replace operation
        if (addAndRemoveChangeToUndo.changeFields.replaceCodebaseId) {
          // For replace operations, pass the replaceCodebaseId to handle undo
          const addChange = new AddJsxChange(
            addAndRemoveChangeToUndo.changeFields.addJsxChangeFields,
          );
          undoAddJsxChange({
            ...instantUpdateProps,
            change: addChange,
            replaceCodebaseId:
              addAndRemoveChangeToUndo.changeFields.replaceCodebaseId,
          });
        } else {
          // Normal add and remove operation - undo in reverse order

          // First undo the add operation (removes the added element)
          const addChange = new AddJsxChange(
            addAndRemoveChangeToUndo.changeFields.addJsxChangeFields,
          );
          undoAddJsxChange({
            ...instantUpdateProps,
            change: addChange,
          });

          // Then undo the remove operation (restores the removed elements)
          const removeChange = new RemoveJsxChange(
            addAndRemoveChangeToUndo.changeFields.removeJsxChangeFields,
          );
          undoRemoveJsxChange({
            ...instantUpdateProps,
            change: removeChange,
          });
        }

        recreateLookups = true;
      } else if (
        instantUpdateProps.change.type === ChangeType.UPDATE_DESIGN_TOKENS
      ) {
        undoUpdateDesignTokensChange(instantUpdateProps);
      }
    } else {
      if (instantUpdateProps.change.type === ChangeType.UPDATE_STORYBOARD) {
        if (
          (instantUpdateProps.change as UpdateStoryboardsChange).changeFields
            .additionalGroupedChange
        ) {
          instantUpdateProps.change =
            instantUpdateProps.change.changeFields.additionalGroupedChange;
        } else if (
          (instantUpdateProps.change as UpdateStoryboardsChange).changeFields
            .createStoryboardFields?.removeJsxChangeFields
        ) {
          instantUpdateProps.change = new RemoveJsxChange(
            instantUpdateProps.change.changeFields.createStoryboardFields
              .removeJsxChangeFields,
          );
        }
      }

      if (instantUpdateProps.change.type === ChangeType.UPDATE_CLASSES) {
        applyUpdateClassesChange({
          ...instantUpdateProps,
          throwawayUpdatesClasses: throwawayUpdatesClasses.current,
        });
      } else if (
        instantUpdateProps.change.type === ChangeType.UPDATE_DESIGN_TOKENS
      ) {
        applyUpdateDesignTokensChange(instantUpdateProps);
      } else if (instantUpdateProps.change.type === ChangeType.WRAP_DIV) {
        applyWrapDivChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.REMOVE_JSX) {
        applyRemoveJsxChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.EDIT_TEXT) {
        applyEditTextChange(instantUpdateProps);
      } else if (instantUpdateProps.change.type === ChangeType.DUPLICATE) {
        applyDuplicateJsxChange(instantUpdateProps);
        recreateLookups = true;
      } else if (instantUpdateProps.change.type === ChangeType.ADD_JSX) {
        applyAddJsxChange({
          ...instantUpdateProps,
          canvasId: canvasIdRef.current,
        });
        recreateLookups = true;
      } else if (
        instantUpdateProps.change.type === ChangeType.ADD_AND_REMOVE_JSX
      ) {
        // Apply both the remove and add operations for AddAndRemoveJsxChange
        const addAndRemoveChange =
          instantUpdateProps.change as AddAndRemoveJsxChange;

        // Check if this is a replace operation
        if (addAndRemoveChange.changeFields.replaceCodebaseId) {
          // For replace operations, pass the replaceCodebaseId to handle replacement
          const addChange = new AddJsxChange(
            addAndRemoveChange.changeFields.addJsxChangeFields,
          );
          applyAddJsxChange({
            ...instantUpdateProps,
            change: addChange,
            canvasId: canvasIdRef.current,
            replaceCodebaseId:
              addAndRemoveChange.changeFields.replaceCodebaseId,
          });
        } else {
          // Normal add and remove operation
          // First apply the remove operation
          const removeChange = new RemoveJsxChange(
            addAndRemoveChange.changeFields.removeJsxChangeFields,
          );
          applyRemoveJsxChange({
            ...instantUpdateProps,
            change: removeChange,
          });

          // Then apply the add operation
          const addChange = new AddJsxChange(
            addAndRemoveChange.changeFields.addJsxChangeFields,
          );
          applyAddJsxChange({
            ...instantUpdateProps,
            change: addChange,
            canvasId: canvasIdRef.current,
          });
        }

        recreateLookups = true;
      }
    }

    // Important - the undo/redo change objects are re-created each time they are applied, so store the instant update data on the original change object
    coreChange.instantUpdateData = {
      ...coreChange.instantUpdateData,
      ...instantUpdateProps.persistentData.instantUpdateData,
    };
    coreChange.storyboardsAppliedTo = new Set([
      ...coreChange.storyboardsAppliedTo,
      ...instantUpdateProps.persistentData.storyboardsAppliedTo,
    ]);

    if (fromReapply) {
      coreChange.instantUpdateFailed = prevInstantUpdateFailed;
    } else {
      coreChange.instantUpdateFailed =
        coreChange.instantUpdateFailed ||
        instantUpdateProps.persistentData.instantUpdateFailed;
    }

    if (!skipUpdateNavTree) {
      updateAllStoreLookups(storyboardId, navTreeUpdates, recreateLookups);
    }

    if (!fromReapply) {
      updateElementKeysForChange(coreChange, isUndo);
    }
  };

  return {
    applyInstantUpdate,
  };
};

export const useApplyInstantUpdatesForStoryboard = ({
  storyboardId,
  iframeDocRef,
}: UseApplyInstantUpdatesProps) => {
  const { applyInstantUpdate } = useApplyInstantUpdates();

  useInitialRenderEffect(() => {
    const innerApplyInstantUpdate = (
      instantUpdateToApply: AnyChangeLedgerItem,
    ) => {
      if (!iframeDocRef.current) {
        return;
      }

      const newLookups = getCurrentLookups(storyboardId);

      // This already updates the lookups
      applyInstantUpdate({
        navTreeUpdates: newLookups,
        iframeDoc: iframeDocRef.current,
        storyboardId,
        instantUpdateToApply,
      });
    };

    useDesignCommandManager
      .getState()
      .setInstantUpdateChannel(storyboardId, innerApplyInstantUpdate);

    return () => {
      useDesignCommandManager
        .getState()
        .setInstantUpdateChannel(storyboardId, undefined);
    };
  });
};

export const useApplyInstantUpdatesForCanvas = () => {
  const applyInstantUpdate = useCallback(
    (instantUpdateToApply: AnyChangeLedgerItem) => {
      let changeToApply = instantUpdateToApply;
      if (instantUpdateToApply.type === ChangeType.REDO) {
        changeToApply = instantUpdateToApply.changeFields.changeToRedo;
      }

      if (instantUpdateToApply.type === ChangeType.UNDO) {
        const changeToUndo = instantUpdateToApply.changeFields.changeToUndo;
        if (changeToUndo.type === ChangeType.UPDATE_STORYBOARD) {
          undoStoryboardsUpdate(changeToUndo as UpdateStoryboardsChange);
        } else if (changeToUndo.type === ChangeType.WRAP_STORYBOARDS) {
          undoWrapStoryboardsUpdate(changeToUndo as WrapStoryboardsChange);
        }
      } else if (changeToApply.type === ChangeType.UPDATE_STORYBOARD) {
        applyStoryboardsUpdate(changeToApply as UpdateStoryboardsChange);
      } else if (changeToApply.type === ChangeType.WRAP_STORYBOARDS) {
        applyWrapStoryboardsUpdate(changeToApply as WrapStoryboardsChange);
      }
    },
    [],
  );

  useEffect(() => {
    useDesignCommandManager
      .getState()
      .setInstantUpdateChannel("CANVAS", applyInstantUpdate);

    return () => {
      useDesignCommandManager
        .getState()
        .setInstantUpdateChannel("CANVAS", undefined);
    };
  }, [applyInstantUpdate]);
};
